import React from 'react';
import { clsx } from 'clsx';

interface ConfirmModalProps {
  isOpen: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'warning' | 'info' | 'error';
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title = 'Atenção',
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'warning',
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null;

  const variantConfig = {
    warning: {
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      iconPath: 'M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z',
      btnColor: 'bg-amber-600 hover:bg-amber-700',
      ringColor: 'ring-amber-400'
    },
    info: {
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      iconPath: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      btnColor: 'bg-blue-600 hover:bg-blue-700',
      ringColor: 'ring-blue-400'
    },
    error: {
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      iconPath: 'M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z',
      btnColor: 'bg-red-600 hover:bg-red-700',
      ringColor: 'ring-red-400'
    }
  };

  const config = variantConfig[variant];

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className={clsx(
        'bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4 transition-all duration-300 ring-2',
        config.ringColor
      )}>
        <div className="flex flex-col items-center text-center">
          <div className={clsx('w-14 h-14 rounded-full flex items-center justify-center mb-4', config.iconBg)}>
            <svg className={clsx('w-8 h-8', config.iconColor)} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d={config.iconPath} />
            </svg>
          </div>

          <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
          <p className="text-gray-600 text-sm mb-6 whitespace-pre-line">{message}</p>

          <div className="flex gap-3 w-full">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={clsx(
                'flex-1 px-4 py-2.5 rounded-lg text-white font-medium transition-colors shadow-md',
                config.btnColor
              )}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
